/* eslint-disable react/prop-types */
import * as React from 'react';
import { connect } from 'react-redux';
import { omit } from 'lodash';

import { fetchSubscriptionByProductAndOrganizationId } from '../reducers/subscription';

import SubscriptionModal from './SubscriptionModal';
import { withModal } from 'components/modal/ModalHOC';

const mapStateToProps = ({ subscription }) => ({
  subscriptions: subscription.subscriptions,
  preventCheck: subscription.preventCheck
});

const mapDispatchToProps = (dispatch) => ({
  dispatchFetchSubscriptionByProduct: (productName, organizationId) => {
    dispatch(fetchSubscriptionByProductAndOrganizationId(productName, organizationId));
  }
});

/**
 * this HOC will connect subscriptions conditions into React Component
 * @param {*} WrappedComponent: React component to be wrapped subscriptions conditions
 * @param {*} hocProps props for our HOC, following this object:
 * {
 *   action: string, // action to be overrided with subscription
 *   product: string, // type of product CM | INSIGHTS
 *   type: string, // key to connect with backend ex. 'multiUser'
 *   fallback: {
 *     type: string, // for now we only support modal
 *     message: string | React
 *   }
 * }
 * @param {*} useRedux this is for StoryBook or testings, we need the pure component and not with Redux.
 */
export function withSubscription(WrappedComponent, hocProps, useRedux = true) {
  const WrappedComponentWithModal = withModal(WrappedComponent, {
    modalContent: SubscriptionModal,
    width: 'small'
  });

  class Subscription extends React.PureComponent {
    static defaultProps = {
      subscriptionCheckEnabled: true
    };

    static displayName = `SubscriptionHOC(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

    componentDidMount() {
      const { product } = hocProps;
      const { organizationId } = this.props;
      const {
        dispatchFetchSubscriptionByProduct,
        subscriptionCheckEnabled
      } = this.props;

      if (subscriptionCheckEnabled) {
        dispatchFetchSubscriptionByProduct(product, organizationId);
      }
    }

    hasPermission = () => {
      const { product, type } = hocProps;
      const { subscriptions } = this.props;
      const permissions = subscriptions[product];
      const permission = permissions.find(p => p.name === type);

      if (!permission.isLimitReached) {
        return true;
      }

      return false;
    };

    renderAction(props) {
      return (
        <WrappedComponent
          key="component"
          {...props}
        />
      );
    }

    renderFallbackAction(props) {
      const { fallback } = hocProps;
      const propsWithoutAction = omit(props, hocProps.action);

      if (fallback.type === 'modal') {
        return (
          <WrappedComponentWithModal
            key="component"
            {...propsWithoutAction}
            modalContentProps={{ title: fallback.title, message: fallback.message }}
          />
        );
      }

      return null;
    }

    render() {
      const { product } = hocProps;
      const { subscriptions, subscriptionCheckEnabled, preventCheck } = this.props;
      const hasNoSubscriptions = subscriptions[product].length === 0;

      const wrappedComponentProps = omit(this.props,
        'subscriptionCheckEnabled',
        'dispatchFetchSubscriptionByProduct',
        'dispatch',
        'subscriptions',
        'preventCheck',
        'organizationId'
      );

      // some cases we want to return the component without subscriptions checker
      if (!subscriptionCheckEnabled || preventCheck) {
        return (
          <WrappedComponent
            {...wrappedComponentProps}
          />
        );
      }

      if (hasNoSubscriptions) {
        return null;
      }

      if (this.hasPermission()) {
        return this.renderAction(wrappedComponentProps);
      }

      return this.renderFallbackAction(wrappedComponentProps);
    }
  }

  if (!useRedux) {
    return Subscription;
  }

  return connect(mapStateToProps, mapDispatchToProps)(Subscription);
}
